import {
  ImageCarouselArrowButton,
  ImageCarouselContainer,
} from "./ProductPhotos.styled";
import { ProductLabel } from "src/types/graphql.d";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProductLabels } from "../ProductLabels";
import { debounce } from "src/utils";
import { ChevronLeft } from "src/icons";
import classNames from "classnames";
import { useBreakpoints } from "@otrium/core";
import {
  TransformWrapper,
  TransformComponent,
  ReactZoomPanPinchContentRef,
} from "react-zoom-pan-pinch";
import {
  useImageCarouselContext,
  useImageCarouselDispatch,
} from "src/hooks/context/use-image-carousel-context";

type ImageCarouselProps = {
  photos: string[];
  labels?: ProductLabel[];
  identifier?: "regular" | "zoom";
  productName: string;
  handleImageClick?: () => void;
  setZoomOpen?: (isOpen: boolean) => void;
};

function ImageCarousel({
  photos,
  labels,
  identifier = "regular",
  productName,
  handleImageClick,
}: ImageCarouselProps): JSX.Element {
  const {
    updateIdentifier,
    selectedImageIndex,
    isImageZoomed: stateImageZoomed,
  } = useImageCarouselContext();
  const imageCarouselDispatch = useImageCarouselDispatch();

  const isFirstRenderRef = useRef(true);
  const imageCarouselRef = useRef<HTMLDivElement>(null);
  const bulletsRef = useRef<HTMLDivElement>(null);
  const prevSelectedImageIndexRef = useRef<number | null>(null);

  const { isDesktop } = useBreakpoints();

  const [selectorPosition, setSelectorPosition] = useState(0);
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  const numberOfPhotos = useMemo(() => photos.length, [photos.length]);

  const onNextClick = useCallback(() => {
    if (!imageCarouselRef.current) {
      return;
    }

    imageCarouselRef.current.scroll({
      left:
        imageCarouselRef.current.scrollLeft +
        imageCarouselRef.current.clientWidth,
    });
  }, []);

  const onPrevClick = useCallback(() => {
    if (!imageCarouselRef.current) {
      return;
    }

    imageCarouselRef.current.scroll({
      left:
        imageCarouselRef.current.scrollLeft -
        imageCarouselRef.current.clientWidth,
    });
  }, []);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (stateImageZoomed) {
        return;
      }

      if (e.key === "ArrowLeft") {
        onPrevClick();
      } else if (e.key === "ArrowRight") {
        onNextClick();
      }
    },
    [stateImageZoomed, onPrevClick, onNextClick]
  );

  const handleZoomToggle = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      if (!isDesktop) {
        setIsImageZoomed((prev) => !prev);
        return;
      }

      const imageWrapper =
        (event.currentTarget.querySelector(
          ".image-wrapper"
        ) as HTMLDivElement) || null;

      const image =
        (imageWrapper?.querySelector("img") as HTMLImageElement) || null;

      if (!imageWrapper || !image) {
        return;
      }

      const imageOriginalWidth = image.getBoundingClientRect().height;

      imageWrapper.classList.toggle("zoomed");

      const ratio = image.getBoundingClientRect().height / imageOriginalWidth;

      if (imageWrapper.classList.contains("zoomed")) {
        imageWrapper.scroll({
          top: (event.clientY - 80) * ratio,
        });
      }

      imageCarouselDispatch({ type: "TOGGLE_IMAGE_ZOOMED", payload: false });
    },
    [isDesktop, imageCarouselDispatch]
  );

  const handleImageTransformPinchStop = useCallback(
    (ref: ReactZoomPanPinchContentRef) => {
      if (ref.instance.getContext().state.scale > 1) {
        setIsImageZoomed(true);
      } else {
        setIsImageZoomed(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!imageCarouselRef.current) {
      return;
    }

    const imageCarousel = imageCarouselRef.current;

    if (
      identifier !== updateIdentifier ||
      (prevSelectedImageIndexRef.current !== null && identifier === "zoom")
    ) {
      return;
    }

    imageCarousel.scroll({
      left: imageCarousel.clientWidth * selectedImageIndex,
    });
    imageCarousel.style.scrollBehavior = "smooth";
  }, [
    identifier,
    updateIdentifier,
    productName,
    selectedImageIndex,
    stateImageZoomed,
    imageCarouselDispatch,
  ]);

  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!imageCarouselRef.current) {
      return;
    }

    const imageCarousel = imageCarouselRef.current;
    const observer = observerRef.current;

    if (!observer) {
      const imageObserverCallback: IntersectionObserverCallback = (
        entries: IntersectionObserverEntry[]
      ) => {
        if (isFirstRenderRef.current) {
          isFirstRenderRef.current = false;

          return;
        }

        for (const entry of entries) {
          const index = Number(entry.target.getAttribute("data-index"));

          if (
            prevSelectedImageIndexRef.current === index &&
            entry.isIntersecting
          ) {
            break;
          }

          if (entry.isIntersecting) {
            imageCarouselDispatch({
              type: "SET_SELECTED_IMAGE_INDEX",
              payload: {
                selectedImageIndex: index,
                updateIdentifier: identifier === "zoom" ? "regular" : "zoom",
              },
            });

            prevSelectedImageIndexRef.current = index;
            break;
          }
        }
      };

      observerRef.current = new IntersectionObserver(imageObserverCallback, {
        root: imageCarousel,
        threshold: 0.5,
      });

      for (const image of imageCarousel.children) {
        observerRef.current.observe(image);
      }

      return;
    }

    if (imageCarousel && observer && stateImageZoomed) {
      for (const image of imageCarousel.children) {
        observer.unobserve(image);
      }

      observerRef.current = null;

      return;
    }

    return () => {
      if (imageCarousel && observer) {
        for (const image of imageCarousel.children) {
          observer.unobserve(image);
        }
      }
    };
  }, [stateImageZoomed, productName, identifier, imageCarouselDispatch]);

  useEffect(() => {
    const debouncedHandler = debounce(handleKeyPress, 100);

    window.addEventListener("keydown", debouncedHandler);

    return () => {
      window.removeEventListener("keydown", debouncedHandler);
    };
  }, [handleKeyPress]);

  useEffect(() => {
    if (
      !bulletsRef.current ||
      !bulletsRef.current.children[selectedImageIndex]
    ) {
      return;
    }

    const bulletPos =
      bulletsRef.current.children[selectedImageIndex].getBoundingClientRect()
        .left - bulletsRef.current.getBoundingClientRect().left;

    if (selectedImageIndex === 0) {
      bulletsRef.current.classList.add("is-first-position");
    } else {
      bulletsRef.current.classList.remove("is-first-position");
    }

    if (selectedImageIndex === photos.length - 1) {
      bulletsRef.current.classList.add("is-last-position");
    } else {
      bulletsRef.current.classList.remove("is-last-position");
    }

    setSelectorPosition(bulletPos);
  }, [selectedImageIndex, photos.length]);

  return (
    <>
      <ImageCarouselContainer
        numberOfPhotos={numberOfPhotos}
        selectorPosition={selectorPosition}
        className={classNames({
          zoom: identifier === "zoom",
        })}
      >
        <ImageCarouselArrowButton
          onClick={onPrevClick}
          className={classNames({ hidden: selectedImageIndex === 0 })}
        >
          <ChevronLeft />
        </ImageCarouselArrowButton>

        <div
          className={classNames("image-carousel-wrapper", {
            "no-scroll": stateImageZoomed,
          })}
          ref={imageCarouselRef}
        >
          {photos.map((photo, idx) => {
            return (
              <div
                key={`pdp-photo-carousel-${idx}${
                  identifier ? `-${identifier}` : ""
                }`}
                data-index={idx}
                className={classNames("carousel-item", {
                  ["zoomed"]: isImageZoomed,
                })}
                role={
                  identifier === "zoom" || handleImageClick
                    ? "button"
                    : undefined
                }
                tabIndex={
                  identifier === "zoom" || handleImageClick ? 0 : undefined
                }
                onClick={
                  isDesktop && identifier === "zoom"
                    ? handleZoomToggle
                    : handleImageClick
                    ? handleImageClick
                    : undefined
                }
                onKeyDown={() => {}}
              >
                <div className="image-wrapper">
                  {!isDesktop && identifier === "zoom" ? (
                    <TransformWrapper
                      initialScale={1}
                      minScale={1}
                      onPinchingStop={handleImageTransformPinchStop}
                      centerOnInit
                      disablePadding
                      panning={{ disabled: !isImageZoomed }}
                    >
                      <TransformComponent
                        wrapperClass="mobile-zoom-wrapper"
                        contentClass="mobile-zoom-content"
                      >
                        <img src={photo} alt={`${productName} ${idx + 1}`} />
                      </TransformComponent>
                    </TransformWrapper>
                  ) : (
                    <img src={photo} alt={`${productName} ${idx + 1}`} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {labels?.length ? (
          <div className="labels-wrapper">
            <ProductLabels labels={labels} />
          </div>
        ) : null}

        <div
          className={classNames("bullets-container", {
            zoom: identifier === "zoom",
          })}
        >
          <div className="bullets-wrapper" ref={bulletsRef}>
            {photos.map((_, idx) => {
              return (
                <span key={`pdp-photo-bullet-${idx}`} className={`bullet`} />
              );
            })}
          </div>
        </div>

        <ImageCarouselArrowButton
          onClick={onNextClick}
          className={classNames({
            hidden: selectedImageIndex === photos.length - 1,
          })}
        >
          <ChevronLeft />
        </ImageCarouselArrowButton>
      </ImageCarouselContainer>
    </>
  );
}

export { ImageCarousel };
